<template>
  <div
    :class="[
      'template-container',
      {
        'template-container--impersonate': isImpersonate,
        'template-container--loaded': addLoadedClass
      }
    ]"
  >
    <div class="template-container__background-wrapper">
      <div class="template-container__background">
        <slot name="main-background" />
      </div>
    </div>
    <div
      :class="[
        'template-container__main',
        { 'template-container__main--impersonate': isImpersonate }
      ]"
    >
      <RegulatoryUpdateWarning />
      <div class="template-container__main-content">
        <slot name="main" />
      </div>
    </div>
    <div ref="contentSection" class="template-container__content">
      <slot name="content" />
    </div>
  </div>
</template>

<script lang="ts">
import { Component, InjectReactive, Ref, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { isImpersonate } from '@/modules/common/services/partner';
import { RegulatoryUpdateWarning } from '@/modules/common/components/RegulatoryUpdateWarning';
import { throttle } from 'lodash-es';
import { AccountStates } from '@/modules/common/services/http/account';

const UIModule = namespace('UIModule');
const CustomerModule = namespace('CustomerModule');

@Component({ components: { RegulatoryUpdateWarning } })
export default class HomeTemplate extends Vue {
  private removeScrollListener?: Function;

  @Ref()
  readonly contentSection!: HTMLElement;

  @InjectReactive('shouldTriggerHomeAnimations')
  public addLoadedClass!: boolean;

  @CustomerModule.Getter('getAccountStatus')
  readonly accountStatusResponse!: AccountStates;

  @UIModule.Mutation('setIsTransparentNavBar')
  private setIsTransparent!: (isTransparent: boolean) => void;

  @UIModule.Getter('getNavBarRef')
  private getNavBarRef!: Vue;

  get haveAccountStatus() {
    return !!this.accountStatusResponse.accountStatus;
  }

  get isImpersonate() {
    return isImpersonate();
  }

  mounted() {
    this.setIsTransparent(true);
    this.contentScrollPositionListener();
  }

  private contentScrollPositionListener() {
    const navbarRef = this.getNavBarRef;

    const onScroll = throttle(() => {
      const navbarHeight = navbarRef.$el.clientHeight;
      const offsetTop = this.contentSection.getBoundingClientRect().top;
      const isContentAtTop = offsetTop >= navbarHeight;
      const shouldChangeNavbarColor =
        navbarRef.$props.isTransparent !== isContentAtTop;

      if (shouldChangeNavbarColor) {
        this.setIsTransparent(isContentAtTop);
      }
    }, 100);

    if (navbarRef) {
      window.addEventListener('scroll', onScroll);
      this.removeScrollListener = () =>
        window.removeEventListener('scroll', onScroll);
    }
  }

  destroyed() {
    this.setIsTransparent(false);
    this.removeScrollListener?.();
  }
}
</script>

<style lang="less" scoped>
@desktop-height-navbar: 64px;
@mobile-height-navbar: 56px;
@main-height: 78vh;
@impersonate-navbar-height: 48px;
@max-width-content: 1008px;
@animation-timing-function: cubic-bezier(0.42, 0, 0.04, 1);
@initial-content-height: 100vh - @main-height;

.template-container {
  width: 100%;
  display: flex;
  flex-direction: column;

  &__main {
    width: 100%;
    position: sticky;
    top: 0;
    min-height: @main-height;
    display: flex;
    align-items: flex-end;
    transition: opacity 0.5s @animation-timing-function;

    &--impersonate {
      min-height: calc(@main-height - @impersonate-navbar-height);
    }

    &-content {
      position: relative;
      width: inherit;
    }
  }

  &__background {
    height: 100%;
    width: 100%;
    transition: transform 1s @animation-timing-function;

    &-wrapper {
      overflow: hidden;
      position: fixed;
      top: 0;
      width: inherit;
      height: 100vh;
    }
  }

  &__content {
    position: relative;
    width: 100%;
    background: @background-secondary;
    transition: transform 1s @animation-timing-function;
    transition-delay: 350ms;
  }

  @-moz-document url-prefix() {
    .body-scroll--hidden &__main-content {
      top: calc(var(--bodyTop) * -1);
    }
  }

  .from-lg({
    &__content {
      background: @background-secondary;
    }
  });

  &:not(&--loaded) & {
    &__main {
      opacity: 0;
    }

    &__background {
      transform: scale(1.15);
    }

    &__content {
      transform: translateY(@initial-content-height);
    }
  }

  &--loaded & {
    &__background {
      transform: scale(1);
    }

    &__main {
      opacity: 1;
    }

    &__content {
      transform: translateY(0);
    }
  }
}
</style>
